import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DocumentUploadPanel
 * Handles upload and display of structured documents (invoices, bonded store POs)
 * for an acquisition lot.  Accepts PDF and image files.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Upload, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { mediaApi } from '../../lib/api';
import { useAccountStore } from '../../stores/account.store';
// ─── Types ────────────────────────────────────────────────────────────────────
const DOCUMENT_CATEGORIES = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'bonded_store_po', label: 'Bonded store PO' },
    { value: 'general', label: 'Other document' },
];
// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function docIcon(mimeType) {
    return mimeType === 'application/pdf'
        ? _jsx(FileText, { className: "h-8 w-8 text-red-400" })
        : _jsx(FileText, { className: "h-8 w-8 text-gray-400" });
}
function categoryLabel(cat) {
    return DOCUMENT_CATEGORIES.find(c => c.value === cat)?.label ?? cat;
}
// ─── Main component ───────────────────────────────────────────────────────────
export function DocumentUploadPanel({ entityType, entityId }) {
    const accountId = useAccountStore(s => s.activeAccount?.id);
    const queryClient = useQueryClient();
    const inputRef = useRef(null);
    const [pending, setPending] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    // ── Fetch existing documents ──────────────────────────────────────────────
    const { data } = useQuery({
        queryKey: ['media', entityType, entityId, 'docs'],
        queryFn: () => mediaApi.getForEntity(entityType, entityId),
        enabled: Boolean(entityId),
        select: d => (d.media ?? []).filter(m => ['invoice', 'bonded_store_po', 'general'].includes(m.mediaCategory) &&
            (m.mimeType === 'application/pdf' || !m.mimeType?.startsWith('image/'))),
    });
    const docs = data ?? [];
    // ── Delete mutation ───────────────────────────────────────────────────────
    const deleteMutation = useMutation({
        mutationFn: (mediaId) => mediaApi.delete(mediaId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['media', entityType, entityId, 'docs'] });
        },
    });
    // ── File selection ────────────────────────────────────────────────────────
    function addFiles(files) {
        if (!files)
            return;
        const newItems = Array.from(files)
            .filter(f => f.type === 'application/pdf' || f.type.startsWith('image/'))
            .map(f => ({
            id: crypto.randomUUID(),
            file: f,
            category: 'invoice',
            status: 'queued',
        }));
        setPending(prev => [...prev, ...newItems]);
    }
    function updateCategory(id, category) {
        setPending(prev => prev.map(d => d.id === id ? { ...d, category } : d));
    }
    function removePending(id) {
        setPending(prev => prev.filter(d => d.id !== id));
    }
    // ── Upload ────────────────────────────────────────────────────────────────
    async function uploadDoc(doc) {
        if (!accountId)
            return;
        setPending(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'uploading' } : d));
        try {
            const prep = await mediaApi.prepareUpload({
                entityType,
                entityId,
                mediaCategory: doc.category,
                mimeType: doc.file.type,
                originalFilename: doc.file.name,
                fileSizeBytes: doc.file.size,
            });
            const uploadRes = await fetch(prep.uploadUrl, {
                method: 'PUT',
                body: doc.file,
                headers: { 'Content-Type': doc.file.type },
            });
            if (!uploadRes.ok)
                throw new Error(`Upload failed: ${uploadRes.status}`);
            await mediaApi.confirmUpload(prep.mediaId);
            setPending(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'done' } : d));
            queryClient.invalidateQueries({ queryKey: ['media', entityType, entityId, 'docs'] });
        }
        catch (err) {
            setPending(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'error', errorMsg: err.message } : d));
        }
    }
    async function handleUploadAll() {
        const queued = pending.filter(d => d.status === 'queued');
        for (const doc of queued)
            await uploadDoc(doc);
        // Remove successfully uploaded items after a short delay
        setTimeout(() => setPending(prev => prev.filter(d => d.status !== 'done')), 1500);
    }
    // ─── Render ────────────────────────────────────────────────────────────────
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: `border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${dragOver ? 'border-wine-400 bg-wine-50' : 'border-gray-200 hover:border-gray-300'}`, onDragOver: e => { e.preventDefault(); setDragOver(true); }, onDragLeave: () => setDragOver(false), onDrop: e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }, onClick: () => inputRef.current?.click(), children: [_jsx(Upload, { className: "h-6 w-6 mx-auto text-gray-400 mb-2" }), _jsxs("p", { className: "text-sm text-gray-500", children: ["Drop PDFs or images here, or ", _jsx("span", { className: "text-wine-600 font-medium", children: "browse" })] }), _jsx("p", { className: "text-xs text-gray-400 mt-1", children: "PDF, JPEG, PNG \u2014 max 50 MB" }), _jsx("input", { ref: inputRef, type: "file", multiple: true, accept: "application/pdf,image/*", className: "hidden", onChange: e => addFiles(e.target.files) })] }), pending.length > 0 && (_jsxs("div", { className: "space-y-2", children: [pending.map(doc => (_jsxs("div", { className: "flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3", children: [docIcon(doc.file.type), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "truncate text-sm font-medium text-gray-700", children: doc.file.name }), _jsx("p", { className: "text-xs text-gray-400", children: formatFileSize(doc.file.size) }), doc.errorMsg && _jsx("p", { className: "text-xs text-red-500 mt-0.5", children: doc.errorMsg })] }), _jsx("select", { value: doc.category, onChange: e => updateCategory(doc.id, e.target.value), disabled: doc.status !== 'queued', className: "text-xs border border-gray-200 rounded px-2 py-1 bg-white", children: DOCUMENT_CATEGORIES.map(c => (_jsx("option", { value: c.value, children: c.label }, c.value))) }), doc.status === 'uploading' && _jsx(Loader2, { className: "h-4 w-4 animate-spin text-wine-500" }), doc.status === 'done' && _jsx("span", { className: "text-xs text-green-600 font-medium", children: "Done" }), doc.status === 'error' && _jsx("span", { className: "text-xs text-red-500 font-medium", children: "Error" }), doc.status === 'queued' && (_jsx("button", { onClick: () => removePending(doc.id), className: "text-gray-400 hover:text-red-500", children: _jsx(Trash2, { className: "h-4 w-4" }) }))] }, doc.id))), pending.some(d => d.status === 'queued') && (_jsxs("button", { onClick: handleUploadAll, className: "btn-primary w-full text-sm", children: ["Upload ", pending.filter(d => d.status === 'queued').length, " document", pending.filter(d => d.status === 'queued').length !== 1 ? 's' : ''] }))] })), docs.length > 0 && (_jsx("div", { className: "divide-y divide-gray-100 rounded-lg border border-gray-100 overflow-hidden", children: docs.map(doc => (_jsxs("div", { className: "flex items-center gap-3 bg-white px-4 py-3", children: [docIcon(doc.mimeType ?? ''), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-sm font-medium text-gray-700 truncate", children: doc.url?.split('/').pop() ?? doc.id }), _jsx("span", { className: "inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 mt-0.5", children: categoryLabel(doc.mediaCategory) })] }), _jsx("a", { href: doc.url, target: "_blank", rel: "noopener noreferrer", className: "text-gray-400 hover:text-wine-600 transition-colors", title: "Open document", children: _jsx(ExternalLink, { className: "h-4 w-4" }) }), _jsx("button", { onClick: () => deleteMutation.mutate(doc.id), disabled: deleteMutation.isPending, className: "text-gray-400 hover:text-red-500 transition-colors", title: "Delete document", children: _jsx(Trash2, { className: "h-4 w-4" }) })] }, doc.id))) })), docs.length === 0 && pending.length === 0 && (_jsx("p", { className: "text-sm text-gray-400 text-center py-2", children: "No documents uploaded yet." }))] }));
}
